import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  Chip,
  Box,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  OutlinedInput,
  Snackbar,
  Alert,
  InputAdornment,
  IconButton,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import { Search as SearchIcon, Clear as ClearIcon } from "@mui/icons-material";
import { useHistory } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import autoGradeClient from "../../../../utils/graphql/autoGradeClient";
import { GET_ALL_EXAM_PAPERS } from "../../../../utils/graphql/services/ai-evaluation/queries";
import { CREATE_EXAM_PAPER_DELIVERY } from "../../../../utils/graphql/services/ai-evaluation/mutations";
import {
  getStateListData,
  getCollegeListData,
  getAcademicYearListData,
  getClassListData,
  getBatchListData,
  getBoardListData,
} from "../../../../utils/metadata-split/metaDataApiFetch";

type CreateExamPaperDeliveryInput = {
  examPaperId: number;
  sortOrder?: number;
  stateIds?: string[];
  collegeIds?: string[];
  academicYearIds?: string[];
  batchIds?: string[];
  classIds?: string[];
  boardIds?: string[];
  scheduledDate?: string;
};

const DEFAULT_INPUT: CreateExamPaperDeliveryInput = {
  examPaperId: 0,
  sortOrder: 1,
  stateIds: [],
  collegeIds: [],
  academicYearIds: [],
  batchIds: [],
  classIds: [],
  boardIds: [],
  scheduledDate: undefined,
};

const CreateExamPaperDeliveryPage = () => {
  const history = useHistory();
  const [form, setForm] = useState<CreateExamPaperDeliveryInput>(DEFAULT_INPUT);
  const [searchTitle, setSearchTitle] = useState("");
  const [options, setOptions] = useState<Record<string, any[]>>({
    stateIds: [],
    collegeIds: [],
    academicYearIds: [],
    batchIds: [],
    classIds: [],
    boardIds: [],
  });
  const [loadingColleges, setLoadingColleges] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const hasLoadedInitialData = useRef(false);

  const {
    data: examPapersData,
    loading: loadingExamPapers,
    error: examPapersError,
    refetch: refetchExamPapers,
  } = useQuery(GET_ALL_EXAM_PAPERS, {
    variables: {
      input: {
        limit: 50,
        offset: 0,
        title: "",
        createdFrom: null,
        createdTill: null,
        updatedFrom: null,
        updatedTill: null,
      },
    },
    client: autoGradeClient,
    fetchPolicy: "cache-first",
    onCompleted: () => {
      hasLoadedInitialData.current = true;
    },
  });

  useEffect(() => {
    if (!hasLoadedInitialData.current) {
      return;
    }

    const timeoutId = setTimeout(() => {
      refetchExamPapers({
        input: {
          limit: 50,
          offset: 0,
          title: searchTitle,
          createdFrom: null,
          createdTill: null,
          updatedFrom: null,
          updatedTill: null,
        },
      });
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTitle, refetchExamPapers]);

  const examPapersList = useMemo(() => {
    return examPapersData?.getExamPapers?.examPapers || [];
  }, [examPapersData]);

  const handleSearchChange = (value: string) => {
    setSearchTitle(value);
  };

  const handleClearSearch = () => {
    setSearchTitle("");
  };

  const [createExamPaperDelivery, { loading: creating }] = useMutation(
    CREATE_EXAM_PAPER_DELIVERY,
    { client: autoGradeClient }
  );

  useEffect(() => {
    async function fetchOptions() {
      try {
        const [states]: any = await getStateListData(() => {});
        const [years]: any = await getAcademicYearListData(() => {});
        const [classes]: any = await getClassListData(() => {});
        const [batches]: any = await getBatchListData(() => {});
        const [boards]: any = await getBoardListData(() => {});

        setOptions((prev) => ({
          ...prev,
          stateIds: states || [],
          academicYearIds: years || [],
          classIds: classes || [],
          batchIds: batches || [],
          boardIds: boards || [],
        }));
      } catch (err) {
        console.error("Failed fetching metadata options:", err);
        setSnackbar({
          open: true,
          message:
            "Failed to load form options. Please refresh the page and try again.",
          severity: "error",
        });
      }
    }
    fetchOptions();
  }, []);

  useEffect(() => {
    async function fetchColleges() {
      if (!form.stateIds || form.stateIds.length === 0) {
        setOptions((prev) => ({ ...prev, collegeIds: [] }));
        return;
      }

      setLoadingColleges(true);
      try {
        const collegePromises = form.stateIds.map((stateId) =>
          getCollegeListData(() => {}, { state_id: stateId })
        );
        const results = await Promise.all(collegePromises);
        const allColleges = results.reduce((acc: any[], [colleges]: any) => {
          if (colleges && Array.isArray(colleges)) {
            return [...acc, ...colleges];
          }
          return acc;
        }, []);
        const uniqueColleges = allColleges.filter(
          (college, index, self) =>
            index === self.findIndex((c) => c.college_id === college.college_id)
        );
        setOptions((prev) => ({ ...prev, collegeIds: uniqueColleges }));
      } catch (err) {
        console.error("Failed fetching colleges", err);
        setOptions((prev) => ({ ...prev, collegeIds: [] }));
        setSnackbar({
          open: true,
          message: "Failed to load colleges",
          severity: "error",
        });
      } finally {
        setLoadingColleges(false);
      }
    }
    fetchColleges();
  }, [form.stateIds]);

  const isValid = typeof form.examPaperId === "number" && form.examPaperId > 0;

  const handleChange = (
    field: keyof CreateExamPaperDeliveryInput,
    value: any
  ) => {
    let newValue = value;
    if (
      [
        "stateIds",
        "collegeIds",
        "academicYearIds",
        "batchIds",
        "classIds",
        "boardIds",
      ].includes(field)
    ) {
      newValue = Array.isArray(value) ? value : [value];
    }

    setForm((prev) => {
      const newForm = { ...prev, [field]: newValue };
      if (field === "stateIds") newForm.collegeIds = [];
      return newForm;
    });
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isValid) {
      setSnackbar({
        open: true,
        message: "Please select an exam paper to continue",
        severity: "error",
      });
      return;
    }

    try {
      const { stateIds, ...payload } = form;

      let finalPayload = { ...payload, source: "ncms1.0" };

      if (!form.scheduledDate && form.examPaperId) {
        const selectedPaper = examPapersList.find(
          (p: any) => p.id === form.examPaperId
        );

        if (selectedPaper?.scheduledDate) {
          let paperScheduledDate = selectedPaper.scheduledDate;
          if (paperScheduledDate) {
            if (paperScheduledDate.includes("T")) {
              paperScheduledDate = paperScheduledDate.split("T")[0];
            }
          }
          finalPayload = {
            ...finalPayload,
            scheduledDate: paperScheduledDate,
          };
        }
      }

      const result = await createExamPaperDelivery({
        variables: { input: finalPayload },
      });

      if (result.data) {
        setSnackbar({
          open: true,
          message: "Exam paper delivery created successfully!",
          severity: "success",
        });
        setTimeout(() => history.push("/ai-evaluation/paper-delivery"), 1500);
      }
    } catch (err: any) {
      console.error("Error creating exam paper delivery:", err);
      const errorMessage =
        err?.graphQLErrors?.[0]?.message ||
        err?.networkError?.message ||
        err?.message ||
        "Failed to create exam paper delivery. Please try again.";

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    }
  };

  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  return (
    <Grid container spacing={2} sx={{ p: 2 }}>
      <Grid item xs={12}>
        <Paper elevation={3} sx={{ p: 2 }}>
          <Typography variant="h6" mb={2}>
            Create Exam Paper Delivery
          </Typography>
          <form onSubmit={handleSave}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  fullWidth
                  options={examPapersList}
                  getOptionLabel={(option: any) =>
                    option.title || `Exam Paper ${option.id}`
                  }
                  value={
                    examPapersList.find(
                      (p: any) => p.id === form.examPaperId
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    handleChange("examPaperId", newValue ? newValue.id : 0);
                  }}
                  inputValue={searchTitle}
                  onInputChange={(event, newInputValue) => {
                    handleSearchChange(newInputValue);
                  }}
                  loading={loadingExamPapers}
                  isOptionEqualToValue={(option: any, value: any) =>
                    option.id === value?.id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search and Select Exam Paper *"
                      placeholder="Type to search exam papers..."
                      error={!isValid && form.examPaperId === 0}
                      helperText={
                        !isValid && form.examPaperId === 0
                          ? "Please select an exam paper"
                          : ""
                      }
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon color="action" />
                          </InputAdornment>
                        ),
                        endAdornment: <>{params.InputProps.endAdornment}</>,
                      }}
                    />
                  )}
                  renderOption={(props, option: any) => (
                    <Box component="li" {...props} key={option.id}>
                      <Box>
                        <Typography variant="body1" fontWeight={500}>
                          {option.title || `Exam Paper ${option.id}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ID: {option.id} | Sections:{" "}
                          {option.totalSections || 0} | Questions:{" "}
                          {option.totalQuestions || 0} | Marks:{" "}
                          {option.totalMarks || 0}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  noOptionsText={
                    loadingExamPapers
                      ? "Loading exam papers..."
                      : examPapersError
                      ? "Error loading exam papers"
                      : searchTitle
                      ? `No exam papers found for "${searchTitle}"`
                      : "No exam papers available"
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Schedule Date (Optional)"
                  type="date"
                  value={form.scheduledDate || ""}
                  onChange={(e) =>
                    handleChange("scheduledDate", e.target.value || undefined)
                  }
                  InputLabelProps={{
                    shrink: true,
                  }}
                  placeholder="YYYY-MM-DD"
                />
              </Grid>
              {/* <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Sort Order"
                  type="number"
                  value={form.sortOrder}
                  onChange={(e) =>
                    handleChange("sortOrder", Number(e.target.value))
                  }
                  inputProps={{ min: 1 }}
                />
              </Grid> */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Select States</InputLabel>
                  <Select
                    multiple
                    value={form.stateIds || []}
                    onChange={(e) => handleChange("stateIds", e.target.value)}
                    input={<OutlinedInput label="Select States" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {(selected as string[]).map((v) => {
                          const s = options.stateIds.find(
                            (s) => s.state_id === v
                          );
                          return (
                            <Chip
                              key={v}
                              label={s?.state_name || v}
                              size="small"
                            />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {options.stateIds.length === 0 ? (
                      <MenuItem disabled>No states available</MenuItem>
                    ) : (
                      options.stateIds.map((s: any) => (
                        <MenuItem key={s.state_id} value={s.state_id}>
                          {s.state_name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Select Colleges</InputLabel>
                  <Select
                    multiple
                    value={form.collegeIds || []}
                    onChange={(e) => handleChange("collegeIds", e.target.value)}
                    input={<OutlinedInput label="Select Colleges" />}
                    disabled={
                      !form.stateIds ||
                      form.stateIds.length === 0 ||
                      loadingColleges
                    }
                    renderValue={(selected) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {(selected as string[]).map((v) => {
                          const c = options.collegeIds.find(
                            (c) => c.college_id === v
                          );
                          return (
                            <Chip
                              key={v}
                              label={c?.college_name || v}
                              size="small"
                            />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {loadingColleges ? (
                      <MenuItem disabled>Loading colleges...</MenuItem>
                    ) : options.collegeIds.length === 0 ? (
                      <MenuItem disabled>
                        {form.stateIds?.length === 0
                          ? "Please select states first"
                          : "No colleges found for selected states"}
                      </MenuItem>
                    ) : (
                      options.collegeIds.map((c: any) => (
                        <MenuItem key={c.college_id} value={c.college_id}>
                          {c.college_name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Grid>
              {[
                { field: "academicYearIds", label: "Academic Years" },
                { field: "classIds", label: "Classes" },
                { field: "batchIds", label: "Batches" },
                { field: "boardIds", label: "Boards" },
              ].map(({ field, label }) => (
                <Grid item xs={12} md={6} key={field}>
                  <FormControl fullWidth>
                    <InputLabel>Select {label}</InputLabel>
                    <Select
                      multiple
                      value={
                        form[field as keyof CreateExamPaperDeliveryInput] || []
                      }
                      onChange={(e) =>
                        handleChange(
                          field as keyof CreateExamPaperDeliveryInput,
                          e.target.value
                        )
                      }
                      input={<OutlinedInput label={`Select ${label}`} />}
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {(selected as string[]).map((v) => {
                            const item = options[field].find((i) => {
                              if (field === "academicYearIds")
                                return i.year_id === v;
                              return (
                                i[
                                  `${field.replace("Ids", "").toLowerCase()}_id`
                                ] === v
                              );
                            });
                            const itemLabel =
                              field === "academicYearIds"
                                ? item?.year_name || v
                                : item?.[
                                    `${field
                                      .replace("Ids", "")
                                      .toLowerCase()}_name`
                                  ] || v;
                            return (
                              <Chip key={v} label={itemLabel} size="small" />
                            );
                          })}
                        </Box>
                      )}
                    >
                      {options[field].length === 0 ? (
                        <MenuItem disabled>
                          No {label.toLowerCase()} available
                        </MenuItem>
                      ) : (
                        options[field].map((o: any) => {
                          const value =
                            field === "academicYearIds"
                              ? o.year_id
                              : o[
                                  `${field.replace("Ids", "").toLowerCase()}_id`
                                ];
                          const itemLabel =
                            field === "academicYearIds"
                              ? o.year_name
                              : o[
                                  `${field
                                    .replace("Ids", "")
                                    .toLowerCase()}_name`
                                ];
                          return (
                            <MenuItem key={value} value={value}>
                              {itemLabel}
                            </MenuItem>
                          );
                        })
                      )}
                    </Select>
                  </FormControl>
                </Grid>
              ))}
              <Grid item xs={12}>
                <Box display="flex" justifyContent="flex-end" mt={2}>
                  <Button
                    variant="outlined"
                    onClick={() => history.goBack()}
                    sx={{ mr: 2 }}
                    disabled={creating}
                  >
                    Close
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={!isValid || creating}
                  >
                    {creating ? (
                      <>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        Creating...
                      </>
                    ) : (
                      "Create Delivery"
                    )}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Grid>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === "success" ? 6000 : 8000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{
            width: "100%",
            minWidth: "300px",
            "& .MuiAlert-message": {
              fontSize: "0.875rem",
              fontWeight: 500,
            },
          }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Grid>
  );
};

export default CreateExamPaperDeliveryPage;
